/**
 * Feature Registry & Subscription Tiers
 *
 * Defines all toggleable features and subscription tiers for the
 * SaaS tenant feature flag system. Both the API and web app import
 * from this single source of truth.
 *
 * Features are organised in a two-tier hierarchy:
 *  - Top-level features (modules, AI, spatial, collaboration)
 *  - Sub-features (children of a parent feature)
 *
 * Subscription tiers pre-set which features are enabled by default.
 * Per-tenant overrides can add or remove individual features.
 */

// ============================================
// Types
// ============================================

/** Categories used to group features in the admin UI */
export type FeatureCategory =
  | 'modules'
  | 'ai'
  | 'spatial'
  | 'collaboration'
  | 'admin';

/** A single toggleable feature in the system */
export interface FeatureDefinition {
  /** Unique key stored in the feature overrides / tier defaults */
  id: string;
  /** Human-readable display name */
  label: string;
  /** Short description shown in the admin UI */
  description: string;
  /** Grouping category for the admin settings page */
  category: FeatureCategory;
  /** If this is a sub-feature, the parent feature id */
  parentFeature?: string;
  /** Lucide icon name (e.g. "Landmark", "Bot") for the admin UI */
  icon?: string;
}

/** A subscription tier that bundles a set of features */
export interface SubscriptionTier {
  /** Unique tier key stored in TenantSettings */
  id: string;
  /** Human-readable tier name */
  name: string;
  /** Short description of the tier */
  description: string;
  /** Feature IDs enabled by default in this tier */
  features: string[];
  /** Display order (ascending: 1 = cheapest) */
  order: number;
}

/** Shape of per-tenant feature overrides stored in DB */
export type FeatureOverrides = Record<string, boolean>;

/** Shape of the resolved effective feature map */
export type EffectiveFeatures = Record<string, boolean>;

// ============================================
// Feature Definitions
// ============================================

export const FEATURE_REGISTRY: FeatureDefinition[] = [
  // ---- Modules ----
  {
    id: 'applications',
    label: 'Grant Funding',
    description: 'Grant application intake, peer review, admin decisions, and post-award reporting.',
    category: 'modules',
    icon: 'ClipboardList',
  },
  {
    id: 'workflowTracker',
    label: 'Workflow Progress Tracker',
    description: 'Visual step-by-step progress bar on application pages showing draft through approved status.',
    category: 'modules',
    icon: 'GitPullRequestArrow',
  },
  {
    id: 'documents',
    label: 'Allow PDF Submissions',
    description: 'Upload, organize, and tag PDF submissions.',
    category: 'modules',
    icon: 'FileText',
  },
  {
    id: 'financialIntegration',
    label: 'Financial Integration',
    description: 'Sync funding data from external accounting systems (Aplos, QuickBooks, CSV import).',
    category: 'modules',
    icon: 'ArrowLeftRight',
  },
  {
    id: 'committeeVoting',
    label: 'Committee Voting',
    description: 'Require committee vote records before approving grant applications. Supports single-vote (Lite) and multi-level organizational (Full) modes.',
    category: 'modules',
    icon: 'Vote',
  },
  {
    id: 'fundingCommitments',
    label: 'Funding Commitments',
    description: 'Track organizational funding commitments per application. Supports single (Lite) and multi-level organizational (Full) modes with dollar or percentage-based pledges.',
    category: 'modules',
    icon: 'HandCoins',
  },

  // ---- AI Features ----
  {
    id: 'aiPrecheck',
    label: 'Precheck',
    description: 'Precheck phase for applications and reports, currently AI-assisted for compliance and completeness scoring.',
    category: 'ai',
    icon: 'Bot',
  },
  {
    id: 'aiChat',
    label: 'Ask Siembra',
    description: 'AI chatbot assistant with module-scoped tools.',
    category: 'ai',
    icon: 'Sparkles',
  },
  {
    id: 'ai-guardian',
    label: 'AI Content Guardian',
    description: 'Local AI security layer that scans and anonymizes sensitive content before it reaches cloud AI services.',
    category: 'ai',
    icon: 'ShieldCheck',
  },

  // ---- Spatial / Analytics ----
  {
    id: 'analytics',
    label: 'Analytics',
    description: 'Unified analytics module: spatial map perspective, tabular explorer, canned reports, saved views, scheduled delivery, and alerts.',
    category: 'spatial',
    icon: 'BarChart3',
  },
  {
    id: 'geographicAnalytics',
    label: 'Geographic Analytics',
    description: 'Choropleth maps, drill-down navigation, heat maps, and coverage gap analysis.',
    category: 'spatial',
    parentFeature: 'analytics',
    icon: 'MapPin',
  },
  {
    id: 'applicationMap',
    label: 'Grant Map',
    description: 'Interactive map showing grant locations.',
    category: 'spatial',
    parentFeature: 'analytics',
    icon: 'Globe',
  },

  // ---- Collaboration ----
  {
    id: 'formSharing',
    label: 'Form Sharing',
    description: 'Time-limited magic links for external form access without authentication.',
    category: 'collaboration',
    icon: 'Link',
  },
  {
    id: 'notifications',
    label: 'Notifications & Activity Feed',
    description: 'In-app notifications, activity logging, and user alerts.',
    category: 'collaboration',
    icon: 'Bell',
  },
  {
    id: 'tawkChat',
    label: 'Live Support',
    description: 'Live chat widget, knowledge base, and support ticket integration via tawk.to.',
    category: 'collaboration',
    icon: 'Headphones',
  },
  {
    id: 'engagement',
    label: 'Engagement',
    description: 'Contact management, audience segmentation, campaigns, and public grant catalog.',
    category: 'collaboration',
    icon: 'Megaphone',
  },
  {
    id: 'engagementEmail',
    label: 'Email Campaigns',
    description: 'Send branded email campaigns to contact audiences via AWS SES.',
    category: 'collaboration',
    parentFeature: 'engagement',
    icon: 'Mail',
  },
  {
    id: 'engagementSms',
    label: 'SMS Campaigns',
    description: 'Send SMS campaigns and two-way keyword messages to contacts via Twilio.',
    category: 'collaboration',
    parentFeature: 'engagement',
    icon: 'MessageSquare',
  },
  {
    id: 'engagementCatalog',
    label: 'Public Grant Catalog',
    description: 'SEO-friendly public page listing open grant programs.',
    category: 'collaboration',
    parentFeature: 'engagement',
    icon: 'Globe',
  },
  {
    id: 'engagementAiDraft',
    label: 'AI Campaign Drafting',
    description: 'Generate campaign content from program metadata using AI.',
    category: 'collaboration',
    parentFeature: 'engagement',
    icon: 'Sparkles',
  },

  // ---- Admin Policies ----
  {
    id: 'adminPasswordReset',
    label: 'Admin Password Reset',
    description: 'Allow admins to set new passwords for email/password users from the Users & Roles settings page.',
    category: 'admin',
    icon: 'KeyRound',
  },

];

/** Set of all valid feature IDs for fast lookup */
export const VALID_FEATURE_IDS = new Set(FEATURE_REGISTRY.map(f => f.id));

// ============================================
// Subscription Tiers
// ============================================

export const SUBSCRIPTION_TIERS: SubscriptionTier[] = [
  {
    id: 'basic',
    name: 'Basic',
    description: 'Core application lifecycle — submissions, reviews, and reports.',
    features: [
      'applications',
      'notifications',
      'ai-guardian',
    ],
    order: 1,
  },
  {
    id: 'standard',
    name: 'Standard',
    description: 'Adds financial tools, analytics, AI scoring, and collaboration.',
    features: [
      'applications',
      'notifications',
      'analytics',
      'applicationMap',
      'formSharing',
      'documents',
      'aiPrecheck',
      'ai-guardian',
      'tawkChat',
      'engagement',
    ],
    order: 2,
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'All core features including full AI, analytics, and collaboration.',
    features: [
      'applications',
      'notifications',
      'analytics',
      'applicationMap',
      'formSharing',
      'documents',
      'aiPrecheck',
      'aiChat',
      'geographicAnalytics',
      'ai-guardian',
      'tawkChat',
      'engagement',
      'engagementEmail',
      'engagementCatalog',
      'engagementAiDraft',
    ],
    order: 3,
  },
];

/** Map of tier ID -> SubscriptionTier for fast lookup */
const TIER_MAP = new Map(SUBSCRIPTION_TIERS.map(t => [t.id, t]));

/** Map of tier ID -> Set of feature IDs for fast membership checks */
const TIER_FEATURE_SETS = new Map(
  SUBSCRIPTION_TIERS.map(t => [t.id, new Set(t.features)])
);

// Validate that every feature referenced in tiers actually exists in the registry.
// This runs at module load time and catches typos early.
for (const tier of SUBSCRIPTION_TIERS) {
  for (const fid of tier.features) {
    if (!VALID_FEATURE_IDS.has(fid)) {
      throw new Error(`SubscriptionTier "${tier.id}" references unknown feature "${fid}"`);
    }
  }
}

// ============================================
// Resolution Logic
// ============================================

/**
 * Resolve the effective feature map for a tenant.
 *
 * 1. Start with the tier defaults (all features in the tier are `true`).
 * 2. Apply per-tenant overrides (`true` adds, `false` removes).
 * 3. Enforce parent/child constraints:
 *    - If a parent feature is disabled, all children are disabled.
 *    - Child overrides cannot override a disabled parent.
 *
 * @param tierId  - The tenant's subscription tier ID (null = no tier, overrides only)
 * @param overrides - Per-tenant feature overrides
 * @returns Record mapping every registered feature ID to its effective enabled state
 */
export function resolveEffectiveFeatures(
  tierId: string | null | undefined,
  overrides: FeatureOverrides = {},
): EffectiveFeatures {
  const result: EffectiveFeatures = {};

  // Determine the tier's default feature set
  const tierFeatures = tierId ? TIER_FEATURE_SETS.get(tierId) : undefined;

  // Build initial map from tier defaults
  for (const feature of FEATURE_REGISTRY) {
    if (tierFeatures) {
      result[feature.id] = tierFeatures.has(feature.id);
    } else {
      // No tier selected — start with everything disabled (overrides-only mode)
      result[feature.id] = false;
    }
  }

  // Apply per-tenant overrides
  for (const [featureId, enabled] of Object.entries(overrides)) {
    if (VALID_FEATURE_IDS.has(featureId)) {
      result[featureId] = enabled;
    }
  }

  // Enforce parent/child constraints: if parent is disabled, children must be too
  for (const feature of FEATURE_REGISTRY) {
    if (feature.parentFeature && !result[feature.parentFeature]) {
      result[feature.id] = false;
    }
  }

  return result;
}

// ============================================
// Helpers
// ============================================

/**
 * Get all child features of a given parent feature.
 */
export function getChildFeatures(parentId: string): FeatureDefinition[] {
  return FEATURE_REGISTRY.filter(f => f.parentFeature === parentId);
}

/**
 * Check if a feature is included in a specific tier's defaults.
 */
export function isFeatureInTier(featureId: string, tierId: string): boolean {
  const tierSet = TIER_FEATURE_SETS.get(tierId);
  return tierSet ? tierSet.has(featureId) : false;
}

/**
 * Get a tier definition by ID.
 * Returns undefined if the tier ID is not valid.
 */
export function getTierById(tierId: string): SubscriptionTier | undefined {
  return TIER_MAP.get(tierId);
}

/**
 * Get a feature definition by ID.
 * Returns undefined if the feature ID is not valid.
 */
export function getFeatureById(featureId: string): FeatureDefinition | undefined {
  return FEATURE_REGISTRY.find(f => f.id === featureId);
}

/**
 * Get features grouped by category (flat arrays).
 * Parent/child relationships are indicated by the `parentFeature` field
 * on each definition; the UI is responsible for nesting them visually.
 */
export function getFeaturesByCategory(): Record<FeatureCategory, FeatureDefinition[]> {
  const grouped: Record<FeatureCategory, FeatureDefinition[]> = {
    modules: [],
    ai: [],
    spatial: [],
    collaboration: [],
    admin: [],
  };

  for (const feature of FEATURE_REGISTRY) {
    grouped[feature.category].push(feature);
  }

  return grouped;
}

/**
 * Category display metadata for the admin UI.
 */
export const FEATURE_CATEGORY_META: Record<FeatureCategory, { label: string; description: string }> = {
  modules: {
    label: 'Modules',
    description: 'Major application modules that can be enabled or disabled.',
  },
  ai: {
    label: 'AI Features',
    description: 'Artificial intelligence powered capabilities.',
  },
  spatial: {
    label: 'Spatial & Analytics',
    description: 'Geographic analytics and map visualizations.',
  },
  collaboration: {
    label: 'Collaboration',
    description: 'Communication, sharing, and notification features.',
  },
  admin: {
    label: 'Administration',
    description: 'Administrative policies and security controls.',
  },
};
